import type {
  AiWorkspaceDefaultsPatch,
  TimesheetAssistant,
  TimesheetAssistantPatch,
} from '@timelyapp/integration-cloud-api-spec';

type RuleMap = Record<string, string> | null | undefined;
type RuleMapPatch = Record<string, string | null> | null | undefined;

const RULE_MAP_KEYS = ['summary_rule_overrides', 'interval_rule_overrides'] as const;
type RuleMapKey = (typeof RULE_MAP_KEYS)[number];

/** The saved workspace-defaults document, as far as the merge reads it. */
export type ExistingWorkspaceDefaults = {
  timesheet_assistant?: Partial<TimesheetAssistant> | null;
  locked_fields?: string[] | null;
};

export type MergedWorkspaceDefaults = {
  timesheet_assistant: Partial<TimesheetAssistant>;
  locked_fields: string[];
};

/**
 * Per-rule merge of a rule-override map.
 * - patch `undefined`: keep the saved map as-is
 * - patch `null`: clear the whole map
 * - patch object: merge over the saved map; a `null` value deletes that rule id.
 *   An empty result collapses to `null`.
 */
export const mergeRuleOverrides = (existing: RuleMap, patch: RuleMapPatch): RuleMap => {
  if (patch === undefined) return existing;
  if (patch === null) return null;

  const merged: Record<string, string> = { ...(existing ?? {}) };
  for (const [ruleId, value] of Object.entries(patch)) {
    if (value === null) delete merged[ruleId];
    else merged[ruleId] = value;
  }
  return Object.keys(merged).length > 0 ? merged : null;
};

/**
 * The client-side twin of TIC's `mergeWorkspaceDefaultsPatch`, used for the
 * optimistic cache update so the UI shows what the server is about to save.
 * Shallow-merges the patch over the saved `timesheet_assistant` (a key present
 * with `null` sets null; absent keys are kept), merges the two rule maps per
 * rule id, and replaces `locked_fields` only when the patch carries it.
 */
export const mergeWorkspaceDefaultsPatch = (
  existing: ExistingWorkspaceDefaults | null | undefined,
  patch: AiWorkspaceDefaultsPatch
): MergedWorkspaceDefaults => {
  const existingAssistant: Partial<TimesheetAssistant> = {
    ...(existing?.timesheet_assistant ?? {}),
  };
  const assistantPatch: TimesheetAssistantPatch = patch.timesheet_assistant ?? {};

  const { summary_rule_overrides, interval_rule_overrides, ...scalarPatch } = assistantPatch;

  const merged: Record<string, unknown> = { ...existingAssistant };
  for (const [key, value] of Object.entries(scalarPatch)) {
    if (value !== undefined) merged[key] = value;
  }

  const ruleMapPatches: Record<RuleMapKey, RuleMapPatch> = {
    summary_rule_overrides,
    interval_rule_overrides,
  };
  for (const key of RULE_MAP_KEYS) {
    if (!(key in assistantPatch)) continue;
    const result = mergeRuleOverrides(existingAssistant[key], ruleMapPatches[key]);
    if (result === undefined) delete merged[key];
    else merged[key] = result;
  }

  return {
    timesheet_assistant: merged as Partial<TimesheetAssistant>,
    locked_fields: patch.locked_fields ?? existing?.locked_fields ?? [],
  };
};
